import i18n from "i18next";
import languageDetector from "i18next-browser-languagedetector";
import { messages } from "./languages/index";
import config from "../config/config";

/**
 * i18n Internationalization Configuration
 * Initializes i18next with language detection, fallback languages, and translation resources
 */
const DEFAULT_LANGUAGE = config.i18n.defaultLanguage;
const LANGUAGE_STORAGE_KEY = "lang";
const I18N_STORAGE_KEY = "i18nextLng";

const getBrowserLanguage = () => {
  if (typeof navigator === "undefined") return null;

  const supported = config.i18n.supportedLanguages || [];
  const browserLanguages = [...(navigator.languages || []), navigator.language].filter(Boolean);

  const normalize = (lang) => lang?.toLowerCase();
  const matchLanguage = (candidate) =>
    supported.find(
      (supportedLang) =>
        normalize(supportedLang) === normalize(candidate) ||
        supportedLang.split("-")[0]?.toLowerCase() === candidate?.split("-")[0]?.toLowerCase()
    );

  for (const candidate of browserLanguages) {
    const match = matchLanguage(candidate);
    if (match) return match;
  }

  return null;
};

/**
 * Initialize and retrieve the user's language preference
 * Falls back to default language if not previously set
 *
 * @returns {string} The language code (e.g., 'pt' or 'en')
 */
function initializeLanguage() {
  const readStoredLanguage = () => {
    const stored = localStorage.getItem(LANGUAGE_STORAGE_KEY) || localStorage.getItem(I18N_STORAGE_KEY);
    if (!stored) return null;

    try {
      const parsed = JSON.parse(stored);
      if (typeof parsed === "string") return parsed;
    } catch {
      // Not JSON, use raw value
    }

    return stored;
  };

  const storedLanguage = readStoredLanguage();
  if (storedLanguage) {
    localStorage.setItem(LANGUAGE_STORAGE_KEY, storedLanguage);
    localStorage.setItem(I18N_STORAGE_KEY, storedLanguage);
    return storedLanguage.split("-")[0];
  }

  const browserLanguage = getBrowserLanguage();
  const chosenLanguage = browserLanguage || DEFAULT_LANGUAGE;

  localStorage.setItem(LANGUAGE_STORAGE_KEY, chosenLanguage);
  localStorage.setItem(I18N_STORAGE_KEY, chosenLanguage);

  return chosenLanguage.split("-")[0];
}

const currentLanguage = initializeLanguage();

i18n.use(languageDetector).init({
  resources: messages,
  lng: currentLanguage,
  fallbackLng: DEFAULT_LANGUAGE.split("-")[0] || "en",
  defaultNS: "translations",
  ns: ["translations"],
  debug: false,
  interpolation: {
    escapeValue: false, // React already protects against XSS
  },
});

export { i18n };
